// Buscas da tela da Gestão do Financeiro (#6499). Cada uma é pequena e independente.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { carregarConferencia, hojeIsoLocal, type CargaConferencia } from '../../src/lib/conferenciaFaturamentoDb';
import { parcelasDaSemana, reguaPorPessoa, type AcaoDaRegua, type ReguaDaPessoa } from '../../src/lib/visaoGeral/financeiro';

const PAGINA = 1000;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** Conferência do Faturamento de um mês ("OUT/2026"); null não busca. */
export function useConferenciaDoMes(mes: string | null): { carga: CargaConferencia | null; erro: string | null } {
  const [estado, setEstado] = useState<{ carga: CargaConferencia | null; erro: string | null }>({ carga: null, erro: null });
  useEffect(() => {
    let vivo = true;
    setEstado({ carga: null, erro: null });
    if (!mes) return;
    carregarConferencia(mes)
      .then(carga => { if (vivo) setEstado({ carga, erro: null }); })
      .catch(e => { console.error('Conferência na Visão Geral:', e); if (vivo) setEstado({ carga: null, erro: 'Não foi possível montar a conferência do faturamento.' }); });
    return () => { vivo = false; };
  }, [mes]);
  return estado;
}

/** Régua de contato humano de hoje, por pessoa, com o nome de cada uma. */
export function useReguaDeHoje(): { pessoas: ReguaDaPessoa[] | null; nomes: Map<string, string>; erro: string | null } {
  const [estado, setEstado] = useState<{ pessoas: ReguaDaPessoa[] | null; nomes: Map<string, string>; erro: string | null }>({ pessoas: null, nomes: new Map(), erro: null });
  useEffect(() => {
    let vivo = true;
    (async () => {
      const hoje = hojeIsoLocal();
      const inicioDoDia = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
      const campos = 'etapa, data_referencia, status, responsavel_id, executado_por, executado_em';
      const [pendentes, feitasHoje] = await Promise.all([
        paginar<AcaoDaRegua>(() => supabase.from('cobranca_acao_manual').select(campos).eq('status', 'pendente').order('id')),
        paginar<AcaoDaRegua>(() => supabase.from('cobranca_acao_manual').select(campos).neq('status', 'pendente').gte('executado_em', inicioDoDia).order('id')),
      ]);
      const pessoas = reguaPorPessoa([...pendentes, ...feitasHoje], hoje);
      const ids = pessoas.map(p => p.responsavelId).filter((x): x is string => !!x);
      const { data: perfis } = ids.length
        ? await supabase.from('profiles').select('id, full_name').in('id', ids)
        : { data: [] as any[] };
      if (vivo) setEstado({ pessoas, nomes: new Map((perfis || []).map((p: any) => [String(p.id), p.full_name || 'Sem nome'])), erro: null });
    })().catch(e => { console.error('Régua de hoje:', e); if (vivo) setEstado({ pessoas: [], nomes: new Map(), erro: 'Não foi possível carregar a régua de cobrança.' }); });
    return () => { vivo = false; };
  }, []);
  return estado;
}

export interface CancelamentosEmAndamento {
  total: number;
  /** Prazo de resposta ao cliente já passou e a resposta não foi enviada. */
  respostaAtrasada: number;
}

export function useCancelamentosEmAndamento(): CancelamentosEmAndamento | null {
  const [estado, setEstado] = useState<CancelamentosEmAndamento | null>(null);
  useEffect(() => {
    let vivo = true;
    supabase.from('solicitacoes_cancelamento')
      .select('id, data_retorno_prevista, resposta_enviada_em')
      .in('status', ['aberta', 'em_andamento'])
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error) { console.error('Cancelamentos em andamento:', error); setEstado({ total: 0, respostaAtrasada: 0 }); return; }
        const hoje = hojeIsoLocal();
        const linhas = (data || []) as { data_retorno_prevista: string | null; resposta_enviada_em: string | null }[];
        setEstado({
          total: linhas.length,
          respostaAtrasada: linhas.filter(l => !l.resposta_enviada_em && l.data_retorno_prevista && String(l.data_retorno_prevista).slice(0, 10) < hoje).length,
        });
      });
    return () => { vivo = false; };
  }, []);
  return estado;
}

export interface AcordosDaSemana {
  semana: { qtd: number; valor: number };
  /** Acordos com parcela vencida sem pagar (status do acordo). */
  emAtraso: number;
}

const ACORDO_ENCERRADO = '(cancelado,expirado,concluido)';

export function useAcordosDaSemana(): AcordosDaSemana | null {
  const [estado, setEstado] = useState<AcordosDaSemana | null>(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      const hoje = hojeIsoLocal();
      const [parcelas, atraso] = await Promise.all([
        supabase.from('parcelamento_parcelas')
          .select('data_vencimento, valor, status, parcelamentos!inner(status)')
          .gte('data_vencimento', hoje)
          .not('parcelamentos.status', 'in', ACORDO_ENCERRADO)
          .order('data_vencimento')
          .limit(1000),
        supabase.from('parcelamentos').select('id', { count: 'exact', head: true }).eq('status', 'em_atraso'),
      ]);
      if (parcelas.error) throw parcelas.error;
      if (vivo) setEstado({ semana: parcelasDaSemana((parcelas.data || []) as any[], hoje), emAtraso: atraso.count ?? 0 });
    })().catch(e => { console.error('Acordos da semana:', e); if (vivo) setEstado({ semana: { qtd: 0, valor: 0 }, emAtraso: 0 }); });
    return () => { vivo = false; };
  }, []);
  return estado;
}
